import { FC, Fragment, useEffect } from "react";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Async
import { getContact } from "app/store/Contacts/Contacts.async";
// Actions
import { ContactsActions } from "app/store/Contacts/Contacts.slice";
// Selectors
import { selectContact } from "app/store/Contacts/Contacts.selectors";
// MUI
import { Box, Divider, Drawer, IconButton, Typography } from "@mui/material";
// Icons
import { Close as CloseIcon } from "@mui/icons-material";
// Components
import { Loader } from "app/components/Utilities";
// Utilities
import { concatAddress, getContactTypeLabel, getPhoneTypeLabel, normalizeHref } from "app/utilities/Utilities";
import { formatPhoneNumber } from "@root/utilities/utlities";

type Props = {
  open: boolean;
  onClose: () => void;
  contactId: number;
}

const ContactDetailsDrawer:FC<Props> = ({ open, onClose, contactId }) => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const contact = useAppSelector(selectContact);

  const renderTitleItem = (value:string) => (
    <Typography
      sx={{ display: 'block', color: 'rgba(0,0,0,0.55)', fontSize: 14, fontWeight: 500 }}
      variant="caption"
    >{value}</Typography>
  );

  const renderTextItem = (value:string, href?:string) => {
    const safeHref = href ? normalizeHref(href) : undefined;
    const props = safeHref ? { component: 'a', href: safeHref, target: '_blank', rel: 'noreferrer' } : {};
    return (
      <Typography
        {...props}
        sx={{ display: 'block', color: 'rgba(0,0,0,0.75)', fontSize: 14, fontWeight: 700 }}
        variant="caption"
      >{value}</Typography>
    )
  };

  useEffect(() => {
    dispatch(getContact(contactId));
    return () => {
      dispatch(ContactsActions.setInitialField('contact'));
    }
    // eslint-disable-next-line
  }, [contactId]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1400 }}
      slotProps={{
        paper: { sx: { width: 480, p: 4 } }
      }}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        gap: 2
      }}>
        <Typography variant="h6">{t('pages.contacts.detailsTitle')}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mx: -4 }} />
      {contact ? (
        <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', rowGap: 2, columnGap: 4, mb: 3 }}>
            {renderTitleItem(t('labels.type'))}
            {renderTextItem(getContactTypeLabel(contact.type))}
            {contact.referenceContact ? (
              <Fragment>
                {renderTitleItem(t('labels.company'))}
                {renderTextItem(contact.referenceContact.name)}
              </Fragment>
            ) : null}
            {renderTitleItem(t('labels.name'))}
            {renderTextItem(contact.name)}
            {renderTitleItem(t('labels.email'))}
            {renderTextItem(contact.email ?? '-', contact.email ? `mailto:${contact.email}` : undefined)}
            {renderTitleItem(t('labels.website'))}
            {renderTextItem(contact.website ?? '-', contact.website ? contact.website : undefined)}
            {renderTitleItem(t('labels.address'))}
            {renderTextItem(concatAddress(contact.address) || '-')}
          </Box>
          <Box sx={{ pt: 2 }}>
            {renderTitleItem(t('labels.phones'))}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content max-content max-content', columnGap: 2, mb: 4, pl: contact.phones ? 4 : 0, pt: 2 }}>
              {contact.phones ? contact.phones.map((phone, index) => (
                <Fragment key={`contact-phone-${contact.id}-${index}`}>
                  {renderTextItem(getPhoneTypeLabel(phone.type))}
                  {renderTextItem(formatPhoneNumber(phone.phoneNumber) || '', `tel:${phone.phoneNumber}`)}
                  {renderTextItem(phone.extension ? `ext. ${phone.extension}` : '')}
                </Fragment>
              )) : renderTextItem('-')}
            </Box>
          </Box>
          {renderTitleItem(t('labels.notes'))}
          {renderTextItem(contact.notes || '-')}
        </Box>
      ) : <Loader />}
    </Drawer>
  )
}

export default ContactDetailsDrawer;
